import { useRef } from "react";
import { Reveal } from "@/components/Reveal";
import { CompassKeyHover } from "@/components/CompassKeyHover";
import { SectionParticles } from "@/components/SectionParticles";
import { SectionBg } from "@/components/SectionBg";
import { CAMPAIGN } from "@/lib/campaign";

const CREDENTIALS = [
  { label: "COMPANY", value: "Kinsfolk Mortgage" },
  { label: "INDIVIDUAL NMLS", value: "#1703351" },
  { label: "COMPANY NMLS", value: "#2610890" },
  { label: "LICENSED IN", value: "Florida" },
];

/* REPLAY SECTION 7 — HOST (id="host") */
export const ReplayHost = () => {
  const portraitRef = useRef(null);

  return (
    <section id="host" className="relative overflow-hidden px-5 md:px-10 lg:px-16 py-28 md:py-36 hairline-t" data-testid="replay-host-section">
      <SectionBg img="bg-home-2.jpg" strength={0.93} />
      <SectionParticles variant="houses" />
      <div className="relative z-10 max-w-6xl mx-auto text-left">
        <p className="font-mono text-[11px] tracking-[0.3em] text-[var(--muted)] mb-12">[005] — YOUR HOST</p>
        <div className="grid grid-cols-1 lg:grid-cols-[0.85fr_1.15fr] gap-12 lg:gap-16 items-start">
          <Reveal>
            <div
              ref={portraitRef}
              className="relative rounded-[8px] overflow-hidden border border-[var(--line)] portrait-pulse md:animate-none"
              data-testid="replay-host-portrait"
            >
              <CompassKeyHover hostRef={portraitRef} />
              <img
                src={CAMPAIGN.headshot}
                alt={`${CAMPAIGN.hostName} — ${CAMPAIGN.hostCompany}`}
                className="relative z-10 w-full h-auto object-cover"
                loading="lazy"
                style={{ aspectRatio: "1 / 1" }}
              />
              <div className="relative z-10 glass-panel px-5 py-4 flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-white">{CAMPAIGN.hostName}</p>
                  <p className="font-mono text-[10px] tracking-[0.12em] text-[var(--muted)]">{CAMPAIGN.hostCompany}</p>
                </div>
                <span className="font-mono text-[10px] tracking-[0.12em] text-[var(--accent)]">
                  NMLS #{CAMPAIGN.hostNmls}
                </span>
              </div>
            </div>
          </Reveal>
          <div>
            <Reveal>
              <h2 className="font-display font-semibold text-3xl md:text-4xl leading-tight text-white mb-8">
                A CLEAR EXPLANATION BEFORE AN INDIVIDUAL APPLICATION.
              </h2>
            </Reveal>
            <Reveal>
              <div className="space-y-5 text-base md:text-lg leading-relaxed text-[rgba(255,255,255,0.78)] mb-10">
                <p>
                  Chaunci Witherspoon, MBA is a Florida mortgage professional with Kinsfolk Mortgage. She has
                  seen what happens when a valuable benefit travels through headlines instead of a plan: the
                  same questions come back, quieter each time, until someone finally puts them in order.
                </p>
                <p>
                  That is why she teaches the education first. Verify → Model → Move puts the general questions
                  on the table before any individual review, so a viewer can hear the full sequence—eligibility,
                  entitlement, buying-power inputs, cash categories, the purchase path—without pressure and
                  without guessing at their own situation.
                </p>
                <p>
                  Personal answers work differently, and she is direct about that. They require a private
                  review and, when appropriate, an application, documentation, and consent. The replay gives
                  you the map's structure; the session is where your specific questions find their place on it.
                </p>
              </div>
            </Reveal>
            <Reveal>
              <div className="grid grid-cols-2 gap-px bg-[var(--line)] border border-[var(--line)] rounded-[6px] overflow-hidden">
                {CREDENTIALS.map((c) => (
                  <div key={c.label} className="bg-[var(--background)] p-5">
                    <p className="font-mono text-[10px] tracking-[0.2em] text-[var(--muted)] mb-1">{c.label}</p>
                    <p className="text-sm md:text-base font-medium text-white">{c.value}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
